import { useState, useEffect } from "react";
import axios from "axios";
import {
    HiOutlineChat,
    HiOutlineClipboardCopy,
    HiCheck
} from "react-icons/hi";

const ITEMS_PER_PAGE = 7;

export default function SMSHistory({ baseUrl, formatDate }) {
    const [messages, setMessages] = useState([]);
    const [outlets, setOutlets] = useState([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [searchTerm, setSearchTerm] = useState("");
    const today = new Date().toLocaleDateString('en-CA')
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);

    const [currentPage, setCurrentPage] = useState(1);
    const [copiedId, setCopiedId] = useState(null);

    const token = localStorage.getItem("token");

    const fetchHistory = async () => {
        if (!token) return;

        setLoading(true);
        setError("");

        try {
            const resp = await axios.post(`${baseUrl}/sms-history`, {
                token,
                start_date: startDate || undefined,
                end_date: endDate || undefined
            });

            if (resp.data.success) {
                setMessages(resp.data.system_entries || []);
                setOutlets(resp.data.outlets || []);
            }
        } catch (err) {
            console.error(err);
            setError("Failed to load SMS history");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHistory();
    }, []);

    // Filtering (search only)
    const filteredMessages = messages.filter((msg) => {
        const text = msg.message_text?.toLowerCase() || "";
        return text.includes(searchTerm.toLowerCase());
    });

    // Pagination
    const totalPages = Math.ceil(filteredMessages.length / ITEMS_PER_PAGE);
    const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginatedMessages = filteredMessages.slice(
        startIdx,
        startIdx + ITEMS_PER_PAGE
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // Copy SMS text
    const copyText = async (msg) => {
        try {
            await navigator.clipboard.writeText(msg.message_text || "");
            setCopiedId(msg.id);
            setTimeout(() => setCopiedId(null), 4000);
        } catch (err) {
            console.error("Copy failed", err);
        }
    };

    if (loading) return <div className="p-5">Loading SMS history...</div>;
    if (error) return <div className="p-5 text-red-600">{error}</div>;

    return (
        <div>
            <h2 className="text-lg font-semibold mb-3">
                SMS Message History
            </h2>

            {/* Date Range Filter */}
            <div className="flex flex-col md:flex-row gap-2 mb-4">
                <input
                    type="text"
                    placeholder="Search SMS text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="border rounded px-3 py-2 w-full md:w-1/3"
                />

                <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="border rounded px-3 py-2 w-full md:w-1/4"
                />

                <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="border rounded px-3 py-2 w-full md:w-1/4"
                />

                <button
                    onClick={fetchHistory}
                    className="bg-indigo-600 text-white px-4 py-2 rounded"
                >
                    view
                </button>
            </div>

            {/* Outlet Summary Table */}
            {outlets.length > 0 && (
                <div className="mb-6">
                    <h3 className="font-semibold mb-2 text-lg">
                        Outlet Message Summary
                    </h3>

                    <div className="overflow-x-auto">
                        <table className="max-w-fit border border-gray-200 text-sm">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="border px-4 py-2 text-left">
                                        Outlet Name
                                    </th>
                                    <th className="border px-4 py-2 text-left">
                                        Total Messages Sent
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {outlets.map((outlet, index) => (
                                    <tr key={index}>
                                        <td className="border px-4 py-2">
                                            {outlet.sender_name}
                                        </td>
                                        <td className="border px-4 py-2 font-semibold">
                                            {outlet.total_numbers}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* SMS History Cards */}
            {filteredMessages.length === 0 ? (
                <p className="text-gray-600">
                    No SMS records found.
                </p>
            ) : (
                <>
                    <div className="space-y-6">
                        {paginatedMessages.map((msg) => (
                            <div
                                key={msg.id}
                                className="border rounded-lg p-3 shadow-sm bg-white"
                            >
                                <div className="flex items-center gap-2">
                                    <HiOutlineChat className="text-indigo-600" />
                                    <span className="font-semibold">
                                        {formatDate(msg.created_at)}
                                    </span>
                                </div>

                                <div className="flex items-center justify-between mb-3">
                                    <div className="text-sm text-gray-500">
                                        Sent to:{" "}
                                        <span className="font-semibold">
                                            {msg.total_numbers}
                                        </span>{" "}
                                        numbers
                                    </div>

                                    <button
                                        onClick={() => copyText(msg)}
                                        className="flex items-center gap-1 text-sm text-indigo-600 hover:underline"
                                    >
                                        {copiedId === msg.id ? (
                                            <>
                                                <HiCheck className="text-green-600 w-4 h-4" /> Copied!
                                            </>
                                        ) : (
                                            <>
                                                <HiOutlineClipboardCopy className="w-4 h-4" /> Copy
                                            </>
                                        )}
                                    </button>
                                </div>

                                <div className="border p-4 rounded-lg text-gray-800 whitespace-pre-wrap">
                                    {msg.message_text}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    <div className="flex justify-end items-center gap-2 mt-4">
                        <button
                            onClick={() =>
                                setCurrentPage((p) => Math.max(1, p - 1))
                            }
                            disabled={currentPage === 1}
                            className={`px-3 py-1 rounded border ${currentPage === 1
                                ? "text-gray-400 border-gray-300"
                                : "text-indigo-600 border-indigo-600"
                                }`}
                        >
                            Previous
                        </button>

                        <span className="text-sm text-gray-600">
                            Page {currentPage} of {totalPages || 1}
                        </span>

                        <button
                            onClick={() =>
                                setCurrentPage((p) =>
                                    Math.min(totalPages, p + 1)
                                )
                            }
                            disabled={currentPage === totalPages}
                            className={`px-3 py-1 rounded border ${currentPage === totalPages
                                ? "text-gray-400 border-gray-300"
                                : "text-indigo-600 border-indigo-600"
                                }`}
                        >
                            Next
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
